import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '../../hooks/useToast';
import { supabase } from '../../lib/supabaseClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const ENABLE_ONBOARDING = process.env.REACT_APP_ENABLE_ONBOARDING_CHECKS === 'true';
  const checkOnboardingStatus = async (userId) => {
    if (!ENABLE_ONBOARDING) return false;
    if (!userId) return false;

    try {
      // Check onboarding status via Supabase Edge Function
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return false;

      // Add a timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/onboarding/user/status`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      const result = await response.json();
      if (result.success && result.data) {
        const { progress, preferences } = result.data;

        // If user has preferences saved, they've completed onboarding
        if (preferences && Object.keys(preferences).length > 0) {
          setNeedsOnboarding(false);
          return false;
        }

        // Check progress record
        if (progress) {
          // User has completed onboarding
          if (progress.completed_at) {
            setNeedsOnboarding(false);
            return false;
          }
          // Check if user completed all steps (backup check)
          if (
            progress.current_step >= 5 &&
            progress.steps_completed &&
            progress.steps_completed.length >= 4
          ) {
            setNeedsOnboarding(false);
            return false;
          }
          // User started but didn't complete
          setNeedsOnboarding(true);
          return true;
        }

        // No progress record - new user needs onboarding
        setNeedsOnboarding(true);
        return true;
      }

      // If the endpoint returns an error or no data, don't force onboarding
      // Silently skip in production unless explicitly enabled
      setNeedsOnboarding(false);
      return false;
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setNeedsOnboarding(false);
      return false;
    }
  };

  // Track ongoing profile fetches to prevent duplicate requests
  const profileFetchCache = React.useRef({});

  const fetchUserProfile = async (userId) => {
    if (!userId) return;

    // Prevent duplicate requests for the same user
    if (profileFetchCache.current[userId]) {
      return profileFetchCache.current[userId];
    }

    const fetchPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          // PGRST116 means no rows returned - profile doesn't exist yet
          if (error.code === 'PGRST116') {
            console.warn('Profile not found for user:', userId);
            setUserType('user'); // Default to regular user
            setProfile({ id: userId, user_type: 'user', display_name: 'User' });
            return;
          }

          console.error('Error fetching profile:', error);
          // Set safe defaults on other errors
          setUserType('user');
          setProfile({ id: userId, user_type: 'user', display_name: 'User' });
          return;
        }

        if (data) {
          setProfile(data);
          setUserType(data.user_type || 'user');

          // Check onboarding status when profile is loaded
          checkOnboardingStatus(userId);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Set safe defaults on exception
        setUserType('user');
        setProfile({ id: userId, user_type: 'user', display_name: 'User' });
      } finally {
        // Clear cache after fetch completes
        delete profileFetchCache.current[userId];
      }
    })();

    profileFetchCache.current[userId] = fetchPromise;
    return fetchPromise;
  };

  useEffect(() => {
    // Handle Supabase email confirmation redirect by setting the session from URL hash tokens
    (async () => {
      try {
        if (typeof window !== 'undefined' && window.location.hash.includes('access_token')) {
          const params = new URLSearchParams(window.location.hash.substring(1));
          const access_token = params.get('access_token');
          const refresh_token = params.get('refresh_token');
          if (access_token && refresh_token) {
            const { data: sessionData, error: setErr } = await supabase.auth.setSession({
              access_token,
              refresh_token,
            });
            if (setErr) {
              console.error('Supabase setSession error:', setErr);
            } else if (sessionData?.session?.user) {
              setUser(sessionData.session.user);

              // Welcome toast
              const displayName =
                sessionData.session.user.user_metadata?.full_name ||
                sessionData.session.user.user_metadata?.username ||
                sessionData.session.user.email?.split('@')[0] ||
                'Welcome';
              try {
                showToast?.(`Welcome, ${displayName}!`, 'success');
              } catch (_) {}

              // Fetch profile to check user type (host/user) and onboarding status
              let userProfile = null;
              try {
                const { data } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', sessionData.session.user.id)
                  .single();

                if (data) {
                  userProfile = data;
                  setProfile(data);
                  setUserType(data.user_type || 'user');
                }
              } catch (profileErr) {
                console.error('Error fetching profile during email confirmation:', profileErr);
              }

              // Determine onboarding status
              let needs = false;
              try {
                needs = await checkOnboardingStatus(sessionData.session.user.id);
              } catch (_) {}

              // Redirect after confirmation based on user type and onboarding status
              if (needs) {
                navigate('/onboarding');
              } else if (userProfile?.is_host || userProfile?.user_type === 'host') {
                // User is a host, redirect to host dashboard
                navigate('/host');
              } else {
                // Regular user, redirect to discover page
                navigate('/discover');
              }
            }
            // Clean hash from the URL so it looks nice
            const cleanUrl =
              window.location.origin + window.location.pathname + window.location.search;
            window.history.replaceState({}, document.title, cleanUrl);
          }
        }
      } catch (e) {
        console.error('AuthContext: error processing redirect token:', e);
      }
    })();
    // Use Supabase auth as the single source of truth
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        // Defer to avoid blocking UI
        setTimeout(() => fetchUserProfile(session.user.id), 0);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        setUser(session.user);
        setTimeout(() => fetchUserProfile(session.user.id), 0);
      } else {
        setUser(null);
        setUserType(null);
        setProfile(null);
        setNeedsOnboarding(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      setUser(data.user);

      // Fetch profile; if missing, backfill a minimal row now that we have a confirmed session
      const { error: fetchErr } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (fetchErr && fetchErr.code !== 'PGRST116') {
        console.error('Error fetching profile on login:', fetchErr);
      }

      if (fetchErr && fetchErr.code === 'PGRST116') {
        const displayName =
          data.user.user_metadata?.full_name ||
          data.user.user_metadata?.username ||
          data.user.email?.split('@')[0] ||
          'User';
        const { error: insertErr } = await supabase.from('profiles').insert({
          id: data.user.id,
          display_name: displayName,
          user_type: 'user',
          city: 'austin',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        if (insertErr && insertErr.code !== '23505') {
          console.error('Profile backfill error on login:', insertErr);
        }
      }

      await fetchUserProfile(data.user.id);

      // Check if user needs onboarding after login
      if (data.user && data.user.id) {
        // Temporarily disabled to prevent login hanging
        // await checkOnboardingStatus(data.user.id);
      }

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      // Ensure Supabase sends email confirmation links back to the correct origin (prod or dev)
      const redirectOrigin =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : process.env.REACT_APP_SITE_URL || 'http://localhost:3000';

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          // Send email confirmations back to our site root
          // Supabase will append the access token as a URL hash fragment
          emailRedirectTo: `${redirectOrigin}`,
        },
      });
      // Handle known duplicate/exists cases explicitly
      if (error) {
        const msg = (error.message || '').toLowerCase();
        if (
          msg.includes('already registered') ||
          msg.includes('already exists') ||
          msg.includes('user exists') ||
          msg.includes('email rate limit')
        ) {
          return {
            success: false,
            code: 'EMAIL_EXISTS',
            error: 'An account with this email already exists',
          };
        }
        throw error;
      }

      // Only attempt immediate profile creation if we have an active session (no email confirmation flow)
      if (data.user) {
        let accessToken = data.session?.access_token || null;
        if (!accessToken) {
          const { data: sess } = await supabase.auth.getSession();
          accessToken = sess?.session?.access_token || null;
        }

        if (accessToken) {
          const { error: profileError } = await supabase.from('profiles').insert({
            id: data.user.id,
            display_name: metadata.full_name || metadata.username || email.split('@')[0],
            user_type: 'user',
            city: 'austin',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });

          if (profileError && profileError.code !== '23505') {
            console.error('Profile creation error:', profileError);
          }
        } else {
          // Email confirmation required: do not treat as signed-in
          return {
            success: true,
            user: data.user,
            requiresEmailConfirmation: true,
          };
        }

        setUser(data.user);
        await fetchUserProfile(data.user.id);
      }

      // If no user object returned, treat as non-success (defensive)
      if (!data?.user) {
        return { success: false, error: 'Registration did not complete' };
      }

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Clear all local/session storage
      localStorage.clear();
      sessionStorage.clear();

      // Force clear state BEFORE supabase signout
      setUser(null);
      setUserType(null);
      setProfile(null);
      setNeedsOnboarding(false);
      setLoading(false);

      // Sign out from Supabase with scope 'local' to ensure complete logout
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.error('Supabase signout error:', error);
        // Continue anyway - state is already cleared
      }

      // Force a small delay to ensure auth state change propagates
      setTimeout(() => {}, 100);

      return null;
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear state even if logout fails
      setUser(null);
      setUserType(null);
      setProfile(null);
      setNeedsOnboarding(false);
      setLoading(false);
    }
  };

  // Get auth token from current session (synchronous)
  const getAuthToken = async () => {
    // First try to get it synchronously if we already have a user
    if (user) {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        return session?.access_token || null;
      } catch (error) {
        console.error('🔑 AuthContext: Error getting fresh session:', error);
        return null;
      }
    }

    return null;
  };

  const value = {
    user,
    userType,
    profile,
    loading,
    needsOnboarding,
    setNeedsOnboarding,
    checkOnboardingStatus,
    login,
    signUp,
    logout,
    getAuthToken,
    fetchUserProfile,
    isAuthenticated: !!user,
    isHost: userType === 'host',
    isAdmin: userType === 'admin',
    isUser: userType === 'user',
  };

  // Log auth state for debugging (commented out for production)
  // console.log('AuthContext - Current state:', {
  //   userType,
  //   isHost: userType === 'host',
  //   user: user?.id || user?.email || 'no user'
  // });

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
